import { useEffect, useMemo, useState } from "react";
import { Activity, AlertOctagon, ArrowRight, ShieldCheck, TrendingUp, X } from "lucide-react";
import { listRiskSnapshots } from "../api";
import type { RiskSnapshot, Vehicle } from "../types";

const levelLabel = {
  low: "BAJO",
  moderate: "MODERADO",
  high: "ALTO",
  critical: "CRÍTICO",
};

export function RiskPanel({
  open,
  onClose,
  vehicles,
  onSelectVehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  onSelectVehicle: (vehicle: Vehicle) => void;
}) {
  const [rows, setRows] = useState<RiskSnapshot[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const refresh = () => {
      setError("");
      listRiskSnapshots()
        .then((riskRows) => {
          setRows(riskRows);
          setSelectedId((current) => current ?? riskRows[0]?.vehicle_id ?? null);
        })
        .catch((cause) => setError(cause instanceof Error ? cause.message : "No fue posible cargar riesgo"));
    };
    refresh();
    window.addEventListener("gsf:risk-updated", refresh);
    return () => window.removeEventListener("gsf:risk-updated", refresh);
  }, [open]);

  const selected = rows.find((row) => row.vehicle_id === selectedId) || rows[0];
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selected?.vehicle_id);
  const highRisk = useMemo(
    () => rows.filter((row) => row.level === "high" || row.level === "critical").length,
    [rows],
  );
  const average = rows.length
    ? Math.round(rows.reduce((sum, row) => sum + row.score, 0) / rows.length)
    : 0;

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel risk-panel" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">INTELIGENCIA EXPLICABLE</span>
            <h2>Riesgo operativo de flotilla</h2>
          </div>
          <button onClick={onClose} aria-label="Cerrar riesgo"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><TrendingUp size={17} /><b>{average}</b> promedio</span>
          <span><AlertOctagon size={17} /><b>{highRisk}</b> alto/crítico</span>
          <span><ShieldCheck size={17} /><b>{rows.filter((row) => row.level === "low").length}</b> bajo</span>
        </section>
        <div className="risk-panel__body">
          <section className="risk-ranking">
            <span className="eyebrow">PRIORIZACIÓN NACIONAL</span>
            {rows.map((row, index) => {
              const vehicle = vehicles.find((item) => item.id === row.vehicle_id);
              return (
                <button className={`${row.level}${selected?.vehicle_id === row.vehicle_id ? " is-selected" : ""}`} onClick={() => setSelectedId(row.vehicle_id)} key={row.id}>
                  <b>{String(index + 1).padStart(2, "0")}</b>
                  <span><strong>{vehicle?.unit_code || row.vehicle_id}</strong><small>{vehicle?.route_name || "Sin ruta"}</small></span>
                  <i><em style={{ width: `${row.score}%` }} /></i>
                  <mark>{row.score}</mark>
                </button>
              );
            })}
          </section>
          <section className="risk-detail">
            {selected && (
              <>
                <div className={`risk-score risk-score--${selected.level}`} style={{ "--risk-score": `${selected.score * 3.6}deg` } as React.CSSProperties}>
                  <span><b>{selected.score}</b><small>{levelLabel[selected.level]}</small></span>
                </div>
                <div className="risk-detail__title">
                  <span className="eyebrow">{selectedVehicle?.unit_code}</span>
                  <h3>{selectedVehicle?.name}</h3>
                  <p>{selectedVehicle?.driver_name} · {selectedVehicle?.route_name}</p>
                </div>
                <div className="risk-factors">
                  <span className="eyebrow">FACTORES DEL PUNTAJE</span>
                  {selected.factors_json.map((factor) => (
                    <article key={factor.code}>
                      <Activity size={13} />
                      <span><strong>{factor.label}</strong><small>{factor.code}</small></span>
                      <b>+{factor.points}</b>
                    </article>
                  ))}
                  {!selected.factors_json.length && <p className="maintenance-empty">SIN FACTORES ACTIVOS</p>}
                </div>
                <div className="risk-recommendation">
                  <ShieldCheck size={18} />
                  <span><small>ACCIÓN RECOMENDADA</small><strong>{selected.recommendation}</strong></span>
                </div>
                {selectedVehicle && <button className="primary-button" onClick={() => { onSelectVehicle(selectedVehicle); onClose(); }}>ABRIR UNIDAD <ArrowRight size={14} /></button>}
              </>
            )}
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
